import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { network, type NetworkOffer } from "@/lib/api";
import Spinner from "@/components/Spinner";
import { Plus, Trash2, X, Edit2, Users, TrendingUp, Store, ArrowLeft } from "lucide-react";

function OfferForm({ offer, onSave, onClose }: {
  offer?: NetworkOffer; onSave: () => void; onClose: () => void;
}) {
  const [form, setForm] = useState({
    title:          offer?.title || "",
    description:    offer?.description || "",
    commission_pct: offer?.commission_pct || 10,
    min_audience:   offer?.min_audience || 0,
    industry:       offer?.industry || "",
    cta_url:        offer?.cta_url || "",
  });
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState<string | null>(null);
  const inp = "w-full px-4 py-2.5 rounded-xl border border-black/[0.08] bg-paper text-sm focus:outline-none focus:ring-2 focus:ring-coral/30";

  const save = async () => {
    if (!form.title) { setError("Title is required"); return; }
    setSaving(true); setError(null);
    try {
      if (offer) { await network.updateOffer(offer.id, form); }
      else { await network.createOffer(form); }
      onSave();
    } catch (e: any) { setError(e.message || "Failed to save"); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-end sm:items-center justify-center p-0 sm:p-4">
      <div className="w-full sm:max-w-lg bg-white sm:rounded-2xl rounded-t-2xl shadow-2xl">
        <div className="flex items-center justify-between px-5 sm:px-6 py-4 border-b border-black/[0.06]">
          <h2 className="font-bold text-ink">{offer ? "Edit offer" : "Create offer"}</h2>
          <button onClick={onClose} className="p-2 rounded-xl text-slate hover:text-ink hover:bg-black/[0.04]"><X size={18} /></button>
        </div>
        <div className="px-5 sm:px-6 py-5 space-y-4 max-h-[70vh] overflow-y-auto">
          {error && <p className="text-xs text-red-500">{error}</p>}
          <div>
            <label className="block text-xs font-bold uppercase tracking-wider text-slate mb-1.5">Title</label>
            <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
              className={inp} placeholder="e.g. Promote our SaaS to your list" />
          </div>
          <div>
            <label className="block text-xs font-bold uppercase tracking-wider text-slate mb-1.5">Description</label>
            <textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
              className={`${inp} resize-none`} placeholder="Describe your product and what you're offering partners…" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-slate mb-1.5">Commission %</label>
              <input type="number" min={1} max={100} value={form.commission_pct}
                onChange={(e) => setForm({ ...form, commission_pct: Number(e.target.value) })} className={inp} />
            </div>
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-slate mb-1.5">Min. audience</label>
              <input type="number" min={0} value={form.min_audience}
                onChange={(e) => setForm({ ...form, min_audience: Number(e.target.value) })} className={inp} placeholder="0 = no minimum" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-slate mb-1.5">Industry</label>
              <input value={form.industry} onChange={(e) => setForm({ ...form, industry: e.target.value })}
                className={inp} placeholder="e.g. SaaS, E-commerce" />
            </div>
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-slate mb-1.5">Conversion URL</label>
              <input value={form.cta_url} onChange={(e) => setForm({ ...form, cta_url: e.target.value })}
                className={inp} placeholder="https://…" />
            </div>
          </div>
        </div>
        <div className="px-5 sm:px-6 py-4 border-t border-black/[0.06] flex gap-3">
          <button onClick={onClose}
            className="flex-1 py-2.5 rounded-full border border-black/[0.08] text-sm font-semibold hover:border-black/[0.14]">Cancel</button>
          <button onClick={save} disabled={saving}
            className="flex-1 py-2.5 rounded-full bg-coral text-white text-sm font-semibold hover:bg-coral-dark disabled:opacity-60 shadow-md shadow-coral/20">
            {saving ? "Saving…" : "Save offer"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function MyOffersPage() {
  const [offers, setOffers]   = useState<NetworkOffer[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<NetworkOffer | "new" | null>(null);
  const [deleting, setDel]    = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    try { const r = await network.myOffers(); setOffers(r.offers || []); }
    catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const handleDelete = async (id: string) => {
    if (!confirm("Delete this offer?")) return;
    setDel(id);
    try { await network.deleteOffer(id); setOffers((p) => p.filter((o) => o.id !== id)); }
    catch (e: any) { alert(e.message || "Delete failed"); }
    finally { setDel(null); }
  };

  return (
    <div>
      <div className="sticky top-0 z-10 bg-paper/90 backdrop-blur-md border-b border-black/[0.06] px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Link to="/network" className="p-2 rounded-xl text-slate hover:text-ink hover:bg-black/[0.04]"><ArrowLeft size={18} /></Link>
          <div>
            <h1 className="text-lg sm:text-xl font-bold text-ink">My Offers</h1>
            <p className="text-xs sm:text-sm text-slate mt-0.5">Offers you've created for other merchants to promote</p>
          </div>
        </div>
        <button onClick={() => setEditing("new")}
          className="inline-flex items-center gap-2 px-4 sm:px-5 py-2.5 rounded-full bg-coral text-white text-sm font-semibold hover:bg-coral-dark transition-colors shadow-md shadow-coral/25">
          <Plus size={15} />
          <span className="hidden sm:inline">Create offer</span>
          <span className="sm:hidden">New</span>
        </button>
      </div>

      <div className="p-4 sm:p-6 lg:p-8 max-w-4xl mx-auto">
        {loading ? <Spinner className="py-20" /> : offers.length === 0 ? (
          <div className="bg-white rounded-2xl border border-black/[0.06] py-16 text-center">
            <Store size={32} className="text-slate opacity-20 mx-auto mb-4" />
            <p className="font-bold text-ink mb-1">No offers yet</p>
            <p className="text-sm text-slate mb-6 max-w-xs mx-auto">
              Create an offer to let other MoreMail merchants promote your product and earn commissions.
            </p>
            <button onClick={() => setEditing("new")}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-coral text-white text-sm font-semibold hover:bg-coral-dark shadow-md shadow-coral/20">
              <Plus size={15} /> Create first offer
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {offers.map((o) => (
              <div key={o.id} className="bg-white rounded-2xl border border-black/[0.06] p-5">
                <div className="flex items-start justify-between gap-3 mb-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      {o.industry && <span className="px-2.5 py-0.5 rounded-full bg-black/[0.04] text-xs font-semibold text-slate">{o.industry}</span>}
                      <span className="px-2.5 py-0.5 rounded-full bg-green-50 text-green-700 text-xs font-bold">{o.commission_pct}% commission</span>
                    </div>
                    <p className="font-bold text-ink truncate">{o.title}</p>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <button onClick={() => setEditing(o)}
                      className="p-2 rounded-xl text-slate hover:text-ink hover:bg-black/[0.04]"><Edit2 size={14} /></button>
                    <button onClick={() => handleDelete(o.id)} disabled={deleting === o.id}
                      className="p-2 rounded-xl text-red-400 hover:bg-red-50 disabled:opacity-40"><Trash2 size={14} /></button>
                  </div>
                </div>
                {o.description && <p className="text-xs text-slate line-clamp-2 mb-3">{o.description}</p>}
                <div className="flex gap-4 text-xs text-slate">
                  {o.partner_count !== undefined && <span className="flex items-center gap-1"><Users size={11} /> {o.partner_count} partners</span>}
                  {o.min_audience > 0 && <span>{o.min_audience.toLocaleString()}+ subs required</span>}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {editing !== null && (
        <OfferForm
          offer={editing === "new" ? undefined : editing}
          onSave={() => { setEditing(null); load(); }}
          onClose={() => setEditing(null)}
        />
      )}
    </div>
  );
}
